import { createModel } from '@rematch/core';

import { RootModel } from '.';

interface TableViewOptions {
  [columnKey: string]: boolean;
}

interface State {
  tableViews: {
    [tableKey: string]: TableViewOptions;
  };
}

export const TOP_TOPICS_TABLE_INITIAL_STATE = {
  'Total views': true,
  'MA Rating': true,
  Videos: true,
  VPH7: true,
  VPH30: false,
  'Combined VPH7': false,
  'Combined VPH30': false,
  'V7 Index': false,
  'V30 Index': false,
  'Average V7': true,
  'Average V30': true,
  V7: false,
  V30: false,
  'Virality Rate': false,
  Rpm: false,
  'eYTV (Videos)': false,
  'e8min (Videos)': false,
  'ViewRate (Videos)': false,
  'Views (Videos)': false,
  'eYTV (Search)': false,
  'e8min (Search)': false,
  'ViewRate (Search)': false,
  Rec7: false,
  Rec30: false,
  ARP7: false,
  ARP30: false,
  'Trending in Views': true,
  Note: false,
};

export const OPTIMISATION_THUMBS_INTROS_FINALISATION_TABLES_INITIAL_STATE = {
  'Variation Name': true,
  Views: true,
  'Video Title': true,
  Thumb: true,
  'Video Duration': false,
  CTR: true,
  'Watchtime (s)': true,
  'e30s Reach': true,
  'e45s Reach': true,
  'e60s Reach': true,
  'e80s Reach': true,
  'e120s Reach': true,
  'e150s Reach': true,
  'e4min Reach': false,
  'e8min Reach': false,
  '30s Reach': false,
  '8min Reach': false,
  '25% Reach': false,
  '50% Reach': false,
  '75% Reach': false,
  '100% Reach': false,
  'Final Value': true,
  eYTV: true,
  'eYTV 4min': true,
  Channel: true,
  'Audience Channels': false,
  'Audience Segment': false,
  'GA Campaign Status': false,
  'Max CPV': true,
  'Campaign Budget': false,
  'Drop Visualization': false,
  Drop: false,
};

export const initialState: State = {
  tableViews: {
    topScriptTopicsTable: { ...TOP_TOPICS_TABLE_INITIAL_STATE },
    topThumbTopicsTable: { ...TOP_TOPICS_TABLE_INITIAL_STATE },
    topTitleTopicsTable: { ...TOP_TOPICS_TABLE_INITIAL_STATE },
    topVideoTypesTable: { ...TOP_TOPICS_TABLE_INITIAL_STATE },
    videosTable: {
      Published: true,
      Views: true,
      Duration: true,
      V1: false,
      V7: false,
      V30: false,
      VPH7: false,
      VPH30: false,
      'V7 Index': false,
      'V30 Index': false,
      Revenue: false,
      RPM: false,
      R7: false,
      R30: false,
      'R7 Index': false,
      'R30 Index': false,
      'eYTV (Videos)': false,
      'e8min (Videos)': false,
      'ViewRate (Videos)': false,
      'Views (Videos)': false,
      'eYTV (Search)': false,
      'e8min (Search)': false,
      'ViewRate (Search)': false,
      'Views (Search)': false,
      Rec7: false,
      Rec30: false,
      ARP7: false,
      ARP30: false,
      'Script topics': true,
      'Thumb topics': true,
      'Title topics': true,
      'Video type': true,
    },
    distributionVideoSummaryTable: {
      'Project ID': true,
      Thumbnail: true,
      'Video Title': true,
      'Production ID': true,
      'Network & Channel': true,
      'Compilation Type': true,
      'Scheduled Date': true,
      'Release Date': true,
      'Total Views': true,
      Director: true,
      Editor: true,
      Publisher: true,
      Archivist: true,
      Link: true,
      'Part In': true,
      'Compilation Group': true,
      'Recipe Status': true,
      Notes: true,
    },
    distributionProjectSummaryTable: {
      'Project ID': true,
      Thumbnail: true,
      'Video Title': true,
      'Production ID': true,
      'Production Team': true,
      'Last Release Date': true,
      Releases: true,
      'Last Release Views': true,
      'Best Release Views': true,
      'Title Topics': true,
      'Video Types': true,
    },
    optimisationThumbsTable: {
      ...OPTIMISATION_THUMBS_INTROS_FINALISATION_TABLES_INITIAL_STATE,
    },
    optimisationIntrosTable: {
      ...OPTIMISATION_THUMBS_INTROS_FINALISATION_TABLES_INITIAL_STATE,
    },
    optimisationFinalisationTable: {
      ...OPTIMISATION_THUMBS_INTROS_FINALISATION_TABLES_INITIAL_STATE,
    },
    optimisationSequenceTable: {
      ...OPTIMISATION_THUMBS_INTROS_FINALISATION_TABLES_INITIAL_STATE,
    },
    optimisationTipsTable: {
      'Variation Name': true,
      Views: true,
      'Scene Duration': true,
      Thumb: true,
      'Video Title': true,
      CTR: true,
      'Watchtime (s)': false,
      'e30s Reach': true,
      'e45s Reach': true,
      'e60s Reach': true,
      'e80s Reach': true,
      'e120s Reach': true,
      'e150s Reach': true,
      'e4min Reach': false,
      'e8min Reach': false,
      '30s Reach': false,
      '8min Reach': false,
      '25% Reach': true,
      '50% Reach': true,
      '75% Reach': true,
      '100% Reach': true,
      'Final Value': true,
      eYTV: false,
      'eYTV 4min': false,
      Channel: true,
      'Audience Channels': false,
      'Max CPV': true,
      'Campaign Budget': false,
      'Drop Visualization': false,
      Drop: false,
    },
    freelancersTable: {
      'Order Nr.': true,
      'Name Surname': true,
      Status: true,
      'Start Date': true,
      Category: true,
      Company: true,
      Department: true,
      Subunit: true,
      Role: true,
      'Employment Type': true,
      'Current Hour Wage': true,
      'Current Day Wage': true,
      'Current Month Wage': true,
      'Current Single Job Wage': true,
      'Work Email': true,
      'Personal Code': true,
      'Birthday Date': true,
      'Phone Number': true,
      'Personal Email': true,
      Address: true,
      'Created By': true,
      'Last Edited': true,
      'Finance Account Number': true,
    },
    invoicesTable: {
      Receiver: true,
      Comments: true,
      'Freelancer Notes': true,
      'Debtor Bank': true,
      'Creditor Bank': true,
      'Creditor Bank Country': true,
      'Payment Type': true,
      Department: true,
      Company: true,
      'Invoice ID': true,
      'Finance Account Number': true,
      'Payment Purpose': true,
      'Amount (€)': true,
      'Amount ($)': true,
      'Amount (£)': true,
      'Received Date': true,
      'Paid Date': true,
      'Created By': true,
      'Last Edited': true,
      Files: true,
      'Proforma Files': true,
      'Additional Files': true,
      Status: true,
    },
    editorialDistributionTable: {
      Cover: true,
      Message: true,
      'Facebook Page': true,
      Status: true,
      'Benchmark Reach': true,
      'Schedule Date': true,
      'Post Date': true,
      'Site Visits': true,
      'Site Visit Benchmark': true,
      'Reach Duration': true,
      Reactions: true,
      Comments: true,
      'Facebook Link': true,
      'Source Link': true,
      Owner: true,
    },
  },
};

export const options = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setTableViewsOptions(
      state,
      payload: {
        tableKey: string;
        newOptions: TableViewOptions;
      },
    ) {
      return {
        ...state,
        tableViews: {
          ...state.tableViews,
          [payload.tableKey]: payload.newOptions,
        },
      };
    },
    resetTableViewsOptions(state, payload: string) {
      return {
        ...state,
        tableViews: {
          ...state.tableViews,
          [payload]: initialState.tableViews[payload],
        },
      };
    },
  },
  effects: (dispatch) => ({}),
});
